import { logger } from '../services/loggingService';

export interface RetryOptions {
  maxRetries?: number;
  initialDelayMs?: number;
  maxDelayMs?: number;
  backoffMultiplier?: number;
  retryableErrors?: string[];
  onRetry?: (attempt: number, error: Error) => void;
}

const DEFAULT_OPTIONS: Required<RetryOptions> = {
  maxRetries: 3,
  initialDelayMs: 1000,
  maxDelayMs: 10000,
  backoffMultiplier: 2,
  retryableErrors: [
    'ECONNRESET',
    'ETIMEDOUT',
    'ENOTFOUND',
    'EAI_AGAIN',
    'fetch failed',
    'network',
    'timeout',
    '429',
    'rate limit',
    '502',
    '503',
    '504',
  ],
  onRetry: () => {},
};

function isRetryableError(error: Error, retryableErrors: string[]): boolean {
  const errorMessage = error.message.toLowerCase();
  return retryableErrors.some((retryableError) =>
    errorMessage.includes(retryableError.toLowerCase())
  );
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export async function retryWithBackoff<T>(
  fn: () => Promise<T>,
  options: RetryOptions = {}
): Promise<T> {
  const opts = { ...DEFAULT_OPTIONS, ...options };
  let lastError: Error | null = null;
  let currentDelay = opts.initialDelayMs;

  for (let attempt = 0; attempt <= opts.maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error instanceof Error ? error : new Error(String(error));

      if (attempt === opts.maxRetries) {
        logger.error('Max retries exceeded', {
          attempts: attempt + 1,
          error: lastError.message,
        });
        throw lastError;
      }

      if (!isRetryableError(lastError, opts.retryableErrors)) {
        logger.info('Non-retryable error encountered', {
          error: lastError.message,
        });
        throw lastError;
      }

      logger.warn('Retryable error encountered, will retry', {
        attempt: attempt + 1,
        maxRetries: opts.maxRetries,
        error: lastError.message,
        delayMs: currentDelay,
      });

      opts.onRetry(attempt + 1, lastError);

      await delay(currentDelay);

      currentDelay = Math.min(
        currentDelay * opts.backoffMultiplier,
        opts.maxDelayMs
      );
    }
  }

  throw lastError || new Error('Retry failed with unknown error');
}

export async function retryWithExponentialBackoff<T>(
  fn: () => Promise<T>,
  maxRetries = 3
): Promise<T> {
  return retryWithBackoff(fn, {
    maxRetries,
    initialDelayMs: 1000,
    maxDelayMs: 10000,
    backoffMultiplier: 2,
  });
}

export function createRetryableFetch(options: RetryOptions = {}) {
  return async function retryableFetch(
    input: RequestInfo | URL,
    init?: RequestInit
  ): Promise<Response> {
    return retryWithBackoff(async () => {
      const response = await fetch(input, init);

      if (response.status === 429) {
        const retryAfter = response.headers.get('Retry-After');
        const delayMs = retryAfter
          ? parseInt(retryAfter, 10) * 1000
          : options.initialDelayMs || 1000;

        throw new Error(
          `Rate limit exceeded (429). Retry after ${delayMs}ms`
        );
      }

      if (response.status >= 500 && response.status < 600) {
        throw new Error(`Server error: ${response.status} ${response.statusText}`);
      }

      if (!response.ok && response.status !== 404) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      return response;
    }, options);
  };
}

export const retryableFetch = createRetryableFetch({
  maxRetries: 3,
  initialDelayMs: 1000,
  maxDelayMs: 10000,
});
